import { ReactNode, useMemo } from 'react';

import { TaskOption } from './createTaskOptions';
import { getTaskOptions, useTaskOption } from './options';

interface GuardProps {
  taskId: string;

  /**
   * A render props function that receives the enabled options
   * for a given taskId
   */
  children: (p: {
    /**
     * Every available option for your task
     */
    options: TaskOption[];

    /**
     *
     * @param options Every action you want to show somewhere
     * @returns Only the enabled actions out of that list. It can be empty
     */
    safeList: (options: TaskOption[]) => TaskOption[];
  }) => ReactNode;
}

interface CheckerProps extends GuardProps {
  options: TaskOption[];

  enabled?: TaskOption[];
}

function CheckOption({ options, enabled = [], ...props }: CheckerProps) {
  const [option, ...otherOptions] = options;

  const currentConfig = useTaskOption(option, props.taskId);

  const onlyEnabled = currentConfig.isEnabled ? [...enabled, option] : enabled;

  if (otherOptions.length)
    return <CheckOption options={otherOptions} {...props} enabled={onlyEnabled} />;

  return (
    <>
      {props.children({
        options: onlyEnabled,
        safeList: (wanted) => wanted.filter((opt) => onlyEnabled.includes(opt)),
      })}
    </>
  );
}

export function EnabledTaskOptionsGuard(props: GuardProps) {
  const options = useMemo(() => Object.keys(getTaskOptions()) as TaskOption[], []);

  return <CheckOption options={options} {...props} />;
}
